import { useMemo, useState } from "react";
import intl from "react-intl-universal";
import { TbCheck, TbDownload, TbInfoCircle } from "react-icons/tb";
import { ValutDialog } from "../../../Components/Molecules/ValutDialog";
import { ValutField, ValutInput } from "../../../Components/Atoms/ValutInput";
import { useToast } from "../../../Hooks/toast";
import { exportExtractCsv } from "./extractFilters";
import { toDateInputValue, fromDateInputValue } from "../../../Components/Molecules/GridHistory";
import moment from "moment";

const getFormatOptions = () => [
  { id: "pdf", label: "PDF" },
  { id: "excel", label: "Excel" },
  { id: "csv", label: "CSV" },
  { id: "ofx", label: "OFX" },
];

const getModeOptions = () => [
  { id: "individual", label: intl.get("extract.exportModeIndividual") },
  { id: "agrupado", label: intl.get("extract.exportModeGrouped") },
];

const getPeriodOptions = () => [
  { id: "hoje", label: intl.get("extract.periodToday") },
  { id: "ultimo-dia", label: intl.get("extract.periodLastDay") },
  { id: "3-dias", label: intl.get("extract.period3Days") },
  { id: "7-dias", label: intl.get("extract.period7Days") },
  { id: "30-dias", label: intl.get("extract.period30Days") },
  { id: "mensal", label: intl.get("extract.periodMonthly") },
  { id: "personalizado", label: intl.get("extract.periodCustom") },
];

const getSortOptions = () => [
  { id: "recente", label: intl.get("extract.sortRecentFirst") },
  { id: "antigo", label: intl.get("extract.sortOldestFirst") },
];

const ToggleGroup = ({ options, value, values, onChange, multi = false, className = "" }) => {
  const isSelected = (id) => (multi ? values.includes(id) : value === id);

  const handleClick = (id) => {
    if (multi) {
      const next = values.includes(id)
        ? values.filter((item) => item !== id)
        : [...values, id];
      onChange(next.length ? next : [id]);
      return;
    }
    onChange(id);
  };

  return (
    <div className={`extract-export-toggle-group ${className}`.trim()}>
      {options.map((option) => {
        const selected = isSelected(option.id);

        return (
          <button
            key={option.id}
            type="button"
            className={`extract-export-toggle ${selected ? "extract-export-toggle--active" : ""}`}
            onClick={() => handleClick(option.id)}
            aria-pressed={selected}
          >
            {selected ? <TbCheck className="extract-export-toggle__check" aria-hidden /> : null}
            {option.label}
          </button>
        );
      })}
    </div>
  );
};

const getPeriodRange = (periodId, customFrom, customTo) => {
  const today = moment().endOf("day");

  switch (periodId) {
    case "hoje":
      return { from: moment().startOf("day"), to: today };
    case "ultimo-dia":
      return {
        from: moment().subtract(1, "day").startOf("day"),
        to: moment().subtract(1, "day").endOf("day"),
      };
    case "3-dias":
      return { from: moment().subtract(2, "days").startOf("day"), to: today };
    case "7-dias":
      return { from: moment().subtract(6, "days").startOf("day"), to: today };
    case "30-dias":
      return { from: moment().subtract(29, "days").startOf("day"), to: today };
    case "mensal":
      return { from: moment().startOf("month"), to: today };
    case "personalizado":
      return {
        from: customFrom ? moment(customFrom).startOf("day") : null,
        to: customTo ? moment(customTo).endOf("day") : null,
      };
    default:
      return { from: null, to: null };
  }
};

const filterItemsByPeriod = (items, periodId, customFrom, customTo) => {
  const { from, to } = getPeriodRange(periodId, customFrom, customTo);
  if (!from && !to) return items;

  return items.filter((item) => {
    const date = moment(item.created_at);
    if (from && date.isBefore(from)) return false;
    if (to && date.isAfter(to)) return false;
    return true;
  });
};

const sortItems = (items, sortId) => {
  const sorted = [...items].sort(
    (a, b) => moment(a.created_at).valueOf() - moment(b.created_at).valueOf(),
  );
  return sortId === "recente" ? sorted.reverse() : sorted;
};

export const ExtractExportModal = ({ open, onClose, items = [] }) => {
  const toast = useToast();
  const [formats, setFormats] = useState(["pdf"]);
  const [mode, setMode] = useState("individual");
  const [period, setPeriod] = useState("7-dias");
  const [sort, setSort] = useState("recente");
  const [customFrom, setCustomFrom] = useState(null);
  const [customTo, setCustomTo] = useState(null);

  const formatOptions = getFormatOptions();
  const modeOptions = getModeOptions();
  const periodOptions = getPeriodOptions();
  const sortOptions = getSortOptions();

  const exportItems = useMemo(() => {
    const filtered = filterItemsByPeriod(items, period, customFrom, customTo);
    return sortItems(filtered, sort);
  }, [items, period, customFrom, customTo, sort]);

  const handleFinalize = () => {
    if (exportItems.length === 0) {
      toast.addToast({
        type: "info",
        title: intl.get("common.nothingToExport"),
        description: intl.get("extract.noEntriesToExport"),
      });
      return;
    }

    const formatLabels = formats.map(
      (id) => formatOptions.find((option) => option.id === id)?.label ?? id,
    );

    if (formats.includes("csv")) {
      exportExtractCsv(exportItems);
    }

    const mockFormats = formats.filter((id) => id !== "csv");
    const modeLabel = mode === "agrupado"
      ? intl.get("extract.exportModeGrouped").toLowerCase()
      : intl.get("extract.exportModeIndividual").toLowerCase();

    if (mockFormats.length > 0) {
      toast.addToast({
        type: "success",
        title: intl.get("extract.exportPreparing"),
        description: intl.get("extract.exportPreparingDescription", {
          count: exportItems.length,
          formats: mockFormats
            .map((id) => formatOptions.find((option) => option.id === id)?.label)
            .join(", "),
          mode: modeLabel,
        }),
      });
    } else {
      toast.addToast({
        type: "success",
        title: intl.get("common.exportComplete"),
        description: intl.get("extract.exportCompleteDescription", {
          count: exportItems.length,
          formats: formatLabels.join(", "),
        }),
      });
    }

    onClose();
  };

  return (
    <ValutDialog
      open={open}
      onClose={onClose}
      title={intl.get("extract.exportModalTitle")}
      icon={<TbDownload className="h-4 w-4" />}
      maxWidth="md"
      bodyClassName="space-y-5"
      footer={(
        <>
          <button type="button" className="grid-history-action-btn sm:min-w-[7rem]" onClick={onClose}>
            {intl.get("words.back")}
          </button>
          <button
            type="button"
            className="grid-history-action-btn grid-history-action-btn--primary sm:min-w-[7rem]"
            onClick={handleFinalize}
          >
            {intl.get("common.finalize")}
          </button>
        </>
      )}
    >
      <p className="m-0 text-sm text-gray-500 dark:text-gray-400">
        {intl.get("extract.exportModalIntro")}
      </p>

      <section className="space-y-2">
        <h3 className="extract-export-section-title">
          {intl.get("extract.exportFormatSection")}
        </h3>
        <ToggleGroup
          options={formatOptions}
          values={formats}
          onChange={setFormats}
          multi
        />
      </section>

      <section className="space-y-2">
        <h3 className="extract-export-section-title flex items-center gap-1.5">
          {intl.get("extract.exportModeSection")}
          <TbInfoCircle className="h-4 w-4 text-gray-400" aria-hidden />
        </h3>
        <ToggleGroup options={modeOptions} value={mode} onChange={setMode} />
      </section>

      <section className="space-y-2">
        <h3 className="extract-export-section-title">
          {intl.get("extract.exportPeriodSection")}
        </h3>
        <ToggleGroup
          options={periodOptions}
          value={period}
          onChange={setPeriod}
          className="extract-export-toggle-group--grid"
        />
        {period === "personalizado" && (
          <div className="grid gap-3 pt-1 sm:grid-cols-2">
            <ValutField label={intl.get("extract.dateFrom")} htmlFor="extract-export-from">
              <ValutInput
                id="extract-export-from"
                type="date"
                value={toDateInputValue(customFrom)}
                onChange={(event) =>
                  setCustomFrom(fromDateInputValue(event.target.value))
                }
              />
            </ValutField>
            <ValutField label={intl.get("extract.dateTo")} htmlFor="extract-export-to">
              <ValutInput
                id="extract-export-to"
                type="date"
                value={toDateInputValue(customTo)}
                onChange={(event) =>
                  setCustomTo(fromDateInputValue(event.target.value))
                }
              />
            </ValutField>
          </div>
        )}
      </section>

      <section className="space-y-2">
        <h3 className="extract-export-section-title">
          {intl.get("extract.exportSortSection")}
        </h3>
        <ToggleGroup options={sortOptions} value={sort} onChange={setSort} />
      </section>
    </ValutDialog>
  );
};
